// Option sets (wire name "form sets") — the Data structure tab's reusable
// picklists. Each set is a titled, ordered list of option values plus a SQL
// column, and its `type` says where it attaches: a FORM set offers its picklist
// at form level, a QUESTION set at question level. Contracts confirmed against
// the backend (2026-09-14): POST /form-sets/list is the usual paged body
// ({ page, limit, search? }, plus the Kind/Last-updated toolbar filters and an
// orderBy) whose response also carries per-kind totals, and
// POST /form-sets/create takes the full set. PATCH /form-sets/update/:id edits
// title + options only (kind and sqlColumn are fixed once created): existing
// options keep their backend id as `formSetOptionId`, options added during the
// edit OMIT that key entirely, and removed existing options are reported by id
// in `deletedOptions`. POST /form-sets/bulk-delete removes any number in one
// call (body { formSetIds }) — a one-row delete sends a one-element array.
//
// Two GET selection feeds sit alongside those four, added for the Data setup
// module's "Manage sets": form-sets/selection-form-sets and
// .../selection-question-sets, each a BARE ARRAY of the catalogue at one
// attachment level. One query per route — form sets and question sets.
//
// File is named after the wire ("form sets"); the exported types use the UI
// name ("option sets"), the formChoicesApi/ResponseSet precedent.

import { baseApi } from '../baseApi';
import { ROUTES } from '../routes';
import type { FormDateFilter } from './formsApi';

/** The confirmed `type` pair — where the set's picklist attaches. */
export type OptionSetKind = 'form_set' | 'question_set';

/** Backend `orderBy.key` values — the confirmed set; members double as the
 *  sortable columns' `key`s. Member names are PascalCase per this repo's other
 *  sort enums; the VALUES are the wire strings. */
export enum OptionSetSortKey {
  Title = 'title',
  SqlColumn = 'sqlColumn',
  Type = 'type',
  UpdatedAt = 'updatedAt',
}

/** One option of a set, as the create payload sends it. Positions are 1-based
 *  and follow the editor's row order. */
export interface OptionSetOptionPayload {
  optionValue: string;
  position: number;
}

/** POST /form-sets/create body. `sqlColumn` is required (the design only
 *  asterisks Name; the confirmed contract needs both) and validated at the form
 *  as a bare lowercase identifier — see option-sets/CreateOptionSetModal. */
export interface CreateOptionSetPayload {
  type: OptionSetKind;
  title: string;
  sqlColumn: string;
  options: OptionSetOptionPayload[];
}

/** One option of the update payload. `formSetOptionId` is the option's backend
 *  id and is OMITTED (not sent null/empty) for options added during the edit. */
export interface UpdateOptionSetOptionPayload {
  formSetOptionId?: string;
  optionValue: string;
  position: number;
}

/** PATCH /form-sets/update/:id body — title + options only (`type` and
 *  `sqlColumn` are fixed once created; the edit form shows both locked).
 *  `deletedOptions` carries the ids of removed existing options. */
export interface UpdateOptionSetPayload {
  title: string;
  options: UpdateOptionSetOptionPayload[];
  deletedOptions: string[];
}

/** POST /form-sets/list body. `search` is omitted when the box is empty; the
 *  toolbar-filter params are omitted when their category has no selection.
 *  `lastUpdated` reuses the shared date-preset contract (FormDateFilter;
 *  "Any time" = send nothing). */
export interface OptionSetQueryArgs {
  page: number;
  limit: number;
  search?: string;
  type?: OptionSetKind[];
  lastUpdated?: FormDateFilter;
  /** yyyy-mm-dd; sent only with CUSTOM. */
  lastUpdatedStart?: string;
  lastUpdatedEnd?: string;
  orderBy?: { key: OptionSetSortKey; type: 'asc' | 'desc' };
}

export interface OptionSetPerson {
  id: string;
  fullName: string;
}

/** One option as the list returns it. */
export interface OptionSetOption {
  id: string;
  value: string;
  position: number;
}

/** One list row. `options` is sorted by `position` in the normalizer, so the
 *  table and a future edit form can render it as-is. */
export interface OptionSet {
  id: string;
  title: string;
  sqlColumn: string;
  kind: OptionSetKind;
  options: OptionSetOption[];
  createdAt: string;
  updatedAt: string;
  lastUpdatedBy: OptionSetPerson | null;
}

/** One set as the Manage-sets catalogue returns it: the paged list's row minus
 *  its kind, timestamps and author. Its own type rather than a Partial of
 *  OptionSet — this payload is complete for what it is, and optionals would
 *  make every consumer re-check fields that are always present. */
export interface OptionSetSelection {
  id: string;
  title: string;
  sqlColumn: string;
  options: OptionSetOption[];
}

/** `totalRecords` is normalized to `total`; the per-kind totals ride along for
 *  when a kind filter or tab counts get wired. */
export interface OptionSetPage {
  items: OptionSet[];
  total: number;
  formSetTotal: number;
  questionSetTotal: number;
}

// ---- raw shapes (per the confirmed response) ----
interface ApiOptionSetPerson {
  _id: string;
  user: { firstName: string; lastName: string };
}

interface ApiOptionSetOption {
  _id: string;
  optionValue: string;
  position: number;
}

interface ApiOptionSet {
  _id: string;
  title: string;
  sqlColumn: string;
  type: OptionSetKind;
  options: ApiOptionSetOption[];
  createdAt: string;
  updatedAt: string;
  lastUpdatedBy: ApiOptionSetPerson | null;
}

/** GET form-sets/selection-{form,question}-sets — the response is a BARE ARRAY
 *  of these, no envelope. */
interface ApiOptionSetSelection {
  _id: string;
  title: string;
  sqlColumn: string;
  options: ApiOptionSetOption[];
}

interface ApiOptionSetPage {
  items?: ApiOptionSet[];
  totalRecords?: number;
  formSetTotal?: number;
  questionSetTotal?: number;
}

const toPerson = (p: ApiOptionSetPerson | null): OptionSetPerson | null => {
  if (!p) return null;
  const fullName = `${p.user.firstName} ${p.user.lastName}`.trim();
  return fullName ? { id: p._id, fullName } : null;
};

const toOptionSet = (s: ApiOptionSet): OptionSet => ({
  id: s._id,
  title: s.title,
  sqlColumn: s.sqlColumn,
  kind: s.type,
  options: s.options
    .map((o) => ({ id: o._id, value: o.optionValue, position: o.position }))
    .sort((a, b) => a.position - b.position),
  createdAt: s.createdAt,
  updatedAt: s.updatedAt,
  lastUpdatedBy: toPerson(s.lastUpdatedBy),
});

const toOptionSetSelection = (s: ApiOptionSetSelection): OptionSetSelection => ({
  id: s._id,
  title: s.title,
  sqlColumn: s.sqlColumn,
  options: s.options
    .map((o) => ({ id: o._id, value: o.optionValue, position: o.position }))
    .sort((a, b) => a.position - b.position),
});

export const formSetsApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getOptionSets: build.query<OptionSetPage, OptionSetQueryArgs>({
      query: (body) => ({ ...ROUTES.formSets.list, data: body }),
      transformResponse: (res: ApiOptionSetPage) => {
        const items = res?.items ?? [];
        return {
          items: items.map(toOptionSet),
          total: res?.totalRecords ?? items.length,
          formSetTotal: res?.formSetTotal ?? 0,
          questionSetTotal: res?.questionSetTotal ?? 0,
        };
      },
      providesTags: [{ type: 'OptionSet', id: 'LIST' }],
    }),
    /** The "Manage sets" catalogue, one endpoint per attachment level — the two
     *  wire routes are distinct, so the split keeps each query one route rather
     *  than a kind switch. FORM sets feed the Data setup module's
     *  "Form meta & SQL" tab.
     *
     *  Both share the LIST tag rather than taking a SELECTION id of their own:
     *  every mutation that changes the paged list changes these catalogues too,
     *  so sharing is what stops a future fifth mutation forgetting to
     *  invalidate them. The cost is one extra refetch of a small static list. */
    getFormSetSelection: build.query<OptionSetSelection[], void>({
      query: () => ({ ...ROUTES.formSets.selectionFormSets }),
      transformResponse: (res: ApiOptionSetSelection[]) => (res ?? []).map(toOptionSetSelection),
      providesTags: [{ type: 'OptionSet', id: 'LIST' }],
    }),
    /** QUESTION sets — the "Form structure" tab's catalogue. Same contract and
     *  tag as its sibling above; only the route differs. */
    getQuestionSetSelection: build.query<OptionSetSelection[], void>({
      query: () => ({ ...ROUTES.formSets.selectionQuestionSets }),
      transformResponse: (res: ApiOptionSetSelection[]) => (res ?? []).map(toOptionSetSelection),
      providesTags: [{ type: 'OptionSet', id: 'LIST' }],
    }),
    createOptionSet: build.mutation<{ _id?: string }, CreateOptionSetPayload>({
      query: (body) => ({ ...ROUTES.formSets.create, data: body }),
      invalidatesTags: [{ type: 'OptionSet', id: 'LIST' }],
    }),
    updateOptionSet: build.mutation<{ _id?: string }, { id: string; body: UpdateOptionSetPayload }>(
      {
        query: ({ id, body }) => ({ ...ROUTES.formSets.update(id), data: body }),
        invalidatesTags: [{ type: 'OptionSet', id: 'LIST' }],
      },
    ),
    // Takes the ids directly and wraps them in the body's `formSetIds` key
    // here, so no call site has to know the envelope.
    bulkDeleteOptionSets: build.mutation<unknown, string[]>({
      query: (formSetIds) => ({ ...ROUTES.formSets.bulkDelete, data: { formSetIds } }),
      invalidatesTags: [{ type: 'OptionSet', id: 'LIST' }],
    }),
  }),
});

export const {
  useGetOptionSetsQuery,
  useGetFormSetSelectionQuery,
  useGetQuestionSetSelectionQuery,
  useCreateOptionSetMutation,
  useUpdateOptionSetMutation,
  useBulkDeleteOptionSetsMutation,
} = formSetsApi;
